import React, { FC, PropsWithChildren, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import "src/firebase/firebase";
import { AuthContext } from "./AuthContext";

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [connectorId, setConnectorId] = useState<number | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [selectedConnectorId, setSelectedConnectorId] = useState<number | null>(() => {
    const stored = localStorage.getItem("sim_selected_connector_id");
    return stored ? parseInt(stored, 10) : null;
  });
  const [idToken, setIdToken] = useState<string | null>(
    localStorage.getItem("id_token")
  );
  const auth = getAuth();

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.slice(1));
    const urlToken = hash.get("token");
    if (urlToken) {
      localStorage.setItem("id_token", urlToken);
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
    }

    // Inbound deep-link from console-ui: ?connector_id=X tells us which
    // company's simulator to open. Persist to localStorage and strip from
    // the URL so a refresh doesn't re-trigger.
    const params = new URLSearchParams(window.location.search);
    const linkedConnector = params.get("connector_id");
    if (linkedConnector && /^\d+$/.test(linkedConnector)) {
      const id = parseInt(linkedConnector, 10);
      localStorage.setItem("sim_selected_connector_id", String(id));
      setSelectedConnectorId(id);
      params.delete("connector_id");
      const remaining = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (remaining ? `?${remaining}` : "") + window.location.hash
      );
    }
  }, []);

  useEffect(() => {
    let refreshInterval: ReturnType<typeof setInterval> | null = null;

    const validateExternalToken = async (token: string) => {
      try {
        const resp = await fetch("/api/v1/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          setIdToken(token);
          setConnectorId(data.connector_id);
          setIsSuperAdmin(data.is_super_admin || false);
          setUser({ email: data.email || "" });
          setIsLoggedIn(true);
          setAccessError(null);
        } else {
          localStorage.removeItem("id_token");
        }
      } catch {
        localStorage.removeItem("id_token");
      }
      setUserLoading(false);
    };

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }

      if (!firebaseUser) {
        const storedToken = localStorage.getItem("id_token");
        if (storedToken) {
          await validateExternalToken(storedToken);
          return;
        }
      }

      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();
        localStorage.setItem("id_token", token);
        setIdToken(token);

        try {
          const resp = await fetch("/api/v1/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (resp.ok) {
            const data = await resp.json();
            setConnectorId(data.connector_id);
            setIsSuperAdmin(data.is_super_admin || false);
            setUser({ email: firebaseUser.email || "" });
            setIsLoggedIn(true);
            setAccessError(null);
          } else if (resp.status === 403) {
            setAccessError(
              "Your account does not have simulator access. Ask an admin to grant manage_app_simulator."
            );
            setUser({ email: firebaseUser.email || "" });
          } else {
            setAccessError("Could not verify simulator access. Is the API running?");
          }
        } catch {
          setAccessError("Could not reach the simulator API.");
        }

        refreshInterval = setInterval(async () => {
          try {
            const fresh = await firebaseUser.getIdToken(true);
            localStorage.setItem("id_token", fresh);
            setIdToken(fresh);
          } catch {
            if (refreshInterval) clearInterval(refreshInterval);
          }
        }, 50 * 60 * 1000);
      } else {
        localStorage.removeItem("id_token");
        setIdToken(null);
        setUser(null);
        setConnectorId(null);
        setIsLoggedIn(false);
      }
      setUserLoading(false);
    });

    return () => {
      unsubscribe();
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, []);

  // Default the active connector to the logged-in user's own. This applies
  // to both regular admins (where the dropdown isn't shown) and super-admins
  // (where the dropdown shows their company name). Manual picks via the
  // dropdown override this until the user clears explicitly.
  useEffect(() => {
    if (connectorId == null) return;
    if (selectedConnectorId != null) return;
    setSelectedConnectorId(connectorId);
    localStorage.setItem("sim_selected_connector_id", String(connectorId));
  }, [connectorId, selectedConnectorId]);

  const handleSetSelectedConnectorId = (id: number | null) => {
    setSelectedConnectorId(id);
    if (id === null) {
      localStorage.removeItem("sim_selected_connector_id");
    } else {
      localStorage.setItem("sim_selected_connector_id", String(id));
    }
  };

  const logout = () => {
    signOut(auth);
    localStorage.removeItem("id_token");
    localStorage.removeItem("sim_selected_connector_id");
    setConnectorId(null);
    setSelectedConnectorId(null);
    setIsSuperAdmin(false);
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userLoading,
        isLoggedIn,
        accessError,
        connectorId,
        isSuperAdmin,
        selectedConnectorId,
        setSelectedConnectorId: handleSetSelectedConnectorId,
        idToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
